import { c as _c } from "react/compiler-runtime";
import { IcoSidebarLeftClosed16 } from '@onefootprint/icons';
import { IconButton } from '@onefootprint/ui';
import * as RadixDialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion, type PanInfo, useReducedMotion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useChatWidgetFullscreenActive from '@/components/agent-chat/hooks/use-chat-widget-fullscreen-active';
import { DISMISS_DISTANCE, DISMISS_VELOCITY, DRAWER_EXIT_TRANSITION, DRAWER_TRANSITION } from '@/components/layout/drawer-motion';
import useMobileNavigation from '@/components/layout/hooks/use-mobile-navigation';
import useMobileLayout from '@/hooks/use-mobile-layout';
import SideNav from '../side-nav';
import useRoutes from '../side-nav/hooks/use-routes/use-routes';
type ResponsiveNavProps = {
  isCollapsed: boolean;
  onSideNavCollapse: (collapsed: boolean) => void;
};
const ResponsiveNav = t0 => {
  const $ = _c(50);
  const {
    isCollapsed,
    onSideNavCollapse
  } = t0;
  const isMobile = useMobileLayout();
  const {
    isOpen,
    setOpen
  } = useMobileNavigation();
  const router = useRouter();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "components.privateLayout.nav"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  const chatActive = useChatWidgetFullscreenActive();
  const reduceMotion = useReducedMotion();
  const panelRef = useRef(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = useRoutes(t2);
  let t4;
  if ($[2] !== router.pathname || $[3] !== t3) {
    const routes = t3.flatMap(_temp);
    let t5;
    if ($[5] !== router.pathname) {
      t5 = t6 => {
        const {
          href,
          activeOn
        } = t6;
        return [href, ...(activeOn ?? [])].some(prefix => router.pathname === prefix || router.pathname.startsWith(`${prefix}/`));
      };
      $[5] = router.pathname;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    t4 = routes.find(t5);
    $[2] = router.pathname;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const route = t4;
  const title = route?.text ?? "Footprint";
  let t5;
  if ($[7] !== reduceMotion) {
    t5 = reduceMotion ? {
      duration: 0
    } : DRAWER_TRANSITION;
    $[7] = reduceMotion;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const transition = t5;
  let t6;
  if ($[9] !== reduceMotion) {
    t6 = reduceMotion ? {
      duration: 0
    } : DRAWER_EXIT_TRANSITION;
    $[9] = reduceMotion;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const exitTransition = t6;
  const {
    events,
    asPath
  } = router;
  let t7;
  let t8;
  if ($[11] !== asPath || $[12] !== events || $[13] !== setOpen) {
    t7 = () => {
      const onNavigate = url => {
        if (url.split("?")[0] !== asPath.split("?")[0]) {
          setOpen(false);
        }
      };
      events.on("routeChangeStart", onNavigate);
      return () => events.off("routeChangeStart", onNavigate);
    };
    t8 = [events, asPath, setOpen];
    $[11] = asPath;
    $[12] = events;
    $[13] = setOpen;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  useEffect(t7, t8);
  if (!isMobile) {
    let t9;
    if ($[16] !== isCollapsed || $[17] !== onSideNavCollapse) {
      t9 = <SideNav isCollapsed={isCollapsed} onSideNavCollapse={onSideNavCollapse} />;
      $[16] = isCollapsed;
      $[17] = onSideNavCollapse;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    return t9;
  }
  let t9;
  if ($[19] !== setOpen) {
    t9 = (_, t10) => {
      const {
        offset,
        velocity
      } = t10;
      if (offset.x < -DISMISS_DISTANCE || velocity.x < -DISMISS_VELOCITY) {
        setOpen(false);
      }
    };
    $[19] = setOpen;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handleDragEnd = t9;
  let t10;
  if ($[21] !== t) {
    t10 = t(_temp2);
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== setOpen) {
    t11 = () => setOpen(true);
    $[23] = setOpen;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <IconButton icon={IcoSidebarLeftClosed16} aria-label={t10} onClick={t11} className="size-11 shrink-0" />;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== title) {
    t13 = <div className="min-w-0 flex-1 truncate py-1 text-label-2 text-primary [text-box:trim-both_cap_alphabetic]">{title}</div>;
    $[28] = title;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== chatActive || $[31] !== t12 || $[32] !== t13) {
    t14 = <header inert={chatActive} className="col-start-1 row-start-1 flex h-12 items-center border-b border-tertiary bg-primary px-2">{t12}{t13}</header>;
    $[30] = chatActive;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== exitTransition || $[35] !== handleDragEnd || $[36] !== isOpen || $[37] !== setOpen || $[38] !== t || $[39] !== transition) {
    t15 = isOpen && <div className="pointer-events-none absolute inset-x-0 bottom-0 top-[env(safe-area-inset-top)] z-drawer"><RadixDialog.Overlay asChild={true} forceMount={true}><motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0,
          pointerEvents: "none",
          transition: exitTransition
        }} transition={transition} className="pointer-events-auto absolute inset-0 bg-(--overlay-backdrop)" /></RadixDialog.Overlay><RadixDialog.Content asChild={true} forceMount={true} aria-describedby={undefined} onOpenAutoFocus={event => {
        event.preventDefault();
        panelRef.current?.focus();
      }}><motion.div ref={panelRef} initial={{
          x: "-100%"
        }} animate={{
          x: 0
        }} exit={{
          x: "-100%",
          pointerEvents: "none",
          transition: exitTransition
        }} transition={transition} drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={{
          left: 1,
          right: 0
        }} dragMomentum={false} onDragEnd={handleDragEnd} onClick={event_0 => {
          if (event_0.target instanceof Element && event_0.target.closest("a[href]")) {
            setOpen(false);
          }
        }} className="pointer-events-auto absolute inset-y-0 left-0 flex w-[min(20rem,calc(100%-4rem))] flex-col overflow-hidden rounded-r-2xl bg-primary pb-[env(safe-area-inset-bottom)] shadow-lg focus:outline-none"><RadixDialog.Title className="sr-only">{t(_temp3)}</RadixDialog.Title><SideNav isCollapsed={false} onSideNavCollapse={() => setOpen(false)} /></motion.div></RadixDialog.Content></div>;
    $[34] = exitTransition;
    $[35] = handleDragEnd;
    $[36] = isOpen;
    $[37] = setOpen;
    $[38] = t;
    $[39] = transition;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t15) {
    t16 = <AnimatePresence>{t15}</AnimatePresence>;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== isOpen || $[44] !== setOpen || $[45] !== t16) {
    t17 = <RadixDialog.Root open={isOpen} onOpenChange={setOpen}>{t16}</RadixDialog.Root>;
    $[43] = isOpen;
    $[44] = setOpen;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t14 || $[48] !== t17) {
    t18 = <>{t14}{t17}</>;
    $[47] = t14;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  return t18;
};
export default ResponsiveNav;
function _temp(group) {
  return group.items;
}
function _temp2($) {
  return $.mobileNavigation.open;
}
function _temp3($_0) {
  return $_0.mobileNavigation.title;
}